import { Component, OnInit, ViewChild, signal, input } from '@angular/core';
import { UntypedFormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import {
  MatTable,
  MatColumnDef,
  MatHeaderCellDef,
  MatHeaderCell,
  MatCellDef,
  MatCell,
  MatHeaderRowDef,
  MatHeaderRow,
  MatRowDef,
  MatRow,
} from '@angular/material/table';
import { Characteristic } from 'src/app/interfaces';
import { collection, onSnapshot, orderBy, query } from 'firebase/firestore';
import { dataStore, db } from 'src/app/app.component';
import { ucFirst } from 'src/app/globals';
import {
  EditRidePreparationItemDialogComponent,
  Field,
} from '../../dialogs/edit-ride-preparation-item-dialog/edit-ride-preparation-item-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { MatCard } from '@angular/material/card';
import { MatFormField, MatLabel, MatSuffix } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatIconButton } from '@angular/material/button';
import { MatRipple } from '@angular/material/core';
import { NgClass } from '@angular/common';
import { MatProgressSpinner } from '@angular/material/progress-spinner';

@Component({
  selector: 'app-characteristics',
  templateUrl: './characteristics.component.html',
  styleUrl: './characteristics.component.scss',
  imports: [
    MatCard,
    MatFormField,
    MatIcon,
    MatLabel,
    MatInput,
    FormsModule,
    ReactiveFormsModule,
    MatIconButton,
    MatSuffix,
    MatTable,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatCellDef,
    MatCell,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRowDef,
    MatRow,
    MatRipple,
    NgClass,
    MatProgressSpinner,
  ],
})
export class CharacteristicsComponent implements OnInit {
  parentData = input<{ processing: boolean }>(undefined);
  @ViewChild('table', { static: false }) table: MatTable<any>;

  characteristics = signal<Characteristic[]>([]);
  filteredCharacteristics = signal<Characteristic[]>([]);
  displayedColumns = ['name', 'description', 'button'];
  totalCharacteristics = signal<number>(0);
  totalActive = signal<number>(0);
  header = true;
  fileUploaded: File;
  searchCtrl = new UntypedFormControl();
  processing = signal(false);
  organisationId = dataStore.selectedOrganisation.id;

  constructor(public dialog: MatDialog) {}

  ngOnInit(): void {
    onSnapshot(
      query(
        collection(db, `organisations/${this.organisationId}/characteristics`),
        orderBy('name'),
      ),
      (snapshot) => {
        const characteristics: Characteristic[] = [];
        this.totalCharacteristics.set(snapshot.size);
        this.totalActive.set(0);
        snapshot.forEach((doc) => {
          const data = doc.data() as Characteristic;
          const id = doc.id;
          data.name = ucFirst(data?.name ?? '');
          characteristics.push({ id, ...data });

          if (data.active) {
            this.totalActive.update((count) => count + 1);
          }
        });
        this.characteristics.set(characteristics);
        this.filteredCharacteristics.set(
          this.filterCharacteristics(this.searchCtrl.value, characteristics),
        );
        if (this.table) {
          this.table.renderRows();
        }
      },
    );
    this.searchCtrl.valueChanges.subscribe((value) => {
      this.filteredCharacteristics.set(this.filterCharacteristics(value, this.characteristics()));
      if (this.table) {
        this.table.renderRows();
      }
    });
  }

  clearSearch() {
    this.searchCtrl.setValue('');
    this.searchCtrl.updateValueAndValidity();
  }

  filterCharacteristics(value: string, array: Characteristic[]): Characteristic[] {
    if (!value || value === '') {
      return array;
    }
    const filterValue = value.toLowerCase();
    return array.filter(
      (characteristic) =>
        characteristic.name.toLowerCase().includes(filterValue) ||
        characteristic.description.toLowerCase().includes(filterValue),
    );
  }

  editItem(characteristic?: Characteristic) {
    const fields: Field[] = [
      {
        key: 'name',
        type: 'string',
        label: 'Kenmerknaam',
        required: true,
      },
      {
        key: 'description',
        type: 'string',
        label: 'Omschrijving',
      },
      {
        key: 'active',
        type: 'toggle',
        label: 'Actief',
        required: false,
      },
    ];
    const dialogRef = this.dialog.open(EditRidePreparationItemDialogComponent, {
      width: '400px',
      data: {
        item: characteristic,
        itemLabel: 'kenmerk',
        fields: fields,
      },
    });
  }

  getExportData() {
    const characteristics = this.characteristics().map(
      (characteristic) => characteristic as Characteristic,
    );
    const columns = [
      { name: 'Naam', itemProperty: 'name' },
      { name: 'Omschrijving', itemProperty: 'description' },
      { name: 'Actief', itemProperty: 'active' },
    ];
    return { items: characteristics, columns: columns, options: { sortBy: 'Naam' } };
  }

  getImportConfig() {
    const columnSettings = [
      { columnName: 'naam', itemProperty: 'name', required: true },
      { columnName: 'omschrijving', itemProperty: 'description' },
      { columnName: 'actief', itemProperty: 'active', type: 'boolean', defaultValue: false },
    ];
    const existingData = this.characteristics().map(
      (characteristic) => characteristic as Characteristic,
    );

    return {
      tab: 'characteristics',
      uniqueKey: 'name',
      organisationId: this.organisationId,
      columnSettings: columnSettings,
      existingData: existingData,
    };
  }
}
